import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { 
  translations as fallbackTranslations, 
  t as fallbackT,
  translateCountry as fallbackTranslateCountry,
  translateContinent as fallbackTranslateContinent
} from '../i18n/translations';

const LanguageContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('de');
  const [liveTranslations, setLiveTranslations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load language from localStorage
    const savedLang = localStorage.getItem('wedding_o_mat_language');
    if (savedLang && ['de', 'en', 'es'].includes(savedLang)) {
      setLanguage(savedLang);
    }

    // Load live translations from API
    loadLiveTranslations();
  }, []);

  const loadLiveTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations/live`);
      setLiveTranslations(response.data);
      console.log('✅ Loaded live translations from database', response.data);
    } catch (error) {
      console.error('⚠️  Failed to load live translations, using fallback', error);
      setLiveTranslations(null);
    } finally {
      setIsLoading(false);
    }
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('wedding_o_mat_language', lang);
  };

  // Translation function that uses live translations if available, falls back to hardcoded
  const t = (key, params = {}) => {
    // Try live translations first
    if (liveTranslations && liveTranslations[language] && liveTranslations[language][key]) {
      let value = liveTranslations[language][key];
      // Replace placeholders like {count}
      if (typeof value === 'string') {
        return value.replace(/{(\w+)}/g, (match, param) => params[param] || match);
      }
      return value;
    }

    // Fallback to hardcoded translations
    return fallbackT(key, language, params);
  };

  // Function to translate countries/destinations
  const translateCountry = (countryName, lang = null) => {
    // Use provided language or default to context language
    const targetLang = lang || language;
    
    // Try live translations first - check both "destination." and "country." keys
    // Backend uses "destination." prefix in translations
    const destinationKey = `destination.${countryName}`;
    if (liveTranslations && liveTranslations[targetLang] && liveTranslations[targetLang][destinationKey]) {
      return liveTranslations[targetLang][destinationKey];
    }
    
    // Also try "country." key for backwards compatibility
    const countryKey = `country.${countryName}`;
    if (liveTranslations && liveTranslations[targetLang] && liveTranslations[targetLang][countryKey]) {
      return liveTranslations[targetLang][countryKey];
    }
    
    // Fallback to hardcoded translations
    return fallbackTranslateCountry(countryName, targetLang);
  };

  // Function to translate continents
  const translateContinent = (continentName, lang = null) => {
    // Use provided language or default to context language
    const targetLang = lang || language;
    // Try live translations first
    const key = `continent.${continentName}`;
    if (liveTranslations && liveTranslations[targetLang] && liveTranslations[targetLang][key]) {
      return liveTranslations[targetLang][key];
    }
    // Fallback to hardcoded translations
    return fallbackTranslateContinent(continentName, targetLang);
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      t,
      translateCountry,
      translateContinent,
      isLoading,
      reloadTranslations: loadLiveTranslations
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
